import type { Vec2, Vec3 } from '../../geo/types';
import type { LinearRGB } from '../../core/color';
import type { PaletteModel } from '../../data/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import type { Chimney, HouseParams } from './types';
import type { Body } from './houseFacades';
import { OPENING, type HouseOpenings, type Opening } from './openings';
import type { ShopFronts } from './shops';
import { computeShadeRatios } from '../../render/surfaceShading';
import { panelPoint, verticalPanel, type Panel } from './panels';
import { meshRectWall, type Hole } from './wallMesh';
import { orientedFace } from './solids';

/* Sources and estimates for every figure below: docs/plans/houses.md, section 8.5. */
export const SHOP_ROOM = {
  /** Brick walls a brick thick, so the room's walls stand clear of the body's and of a neighbour's. */
  wall: 0.2,
  /**
   * Daylight in the room as a share of the street front's: the shop opening is the room's one
   * source, the room's inner surfaces averaged with one bounce (section 8.5: 0.06-0.14, 0.09 for a
   * room 5.1 m wide and 5.5 m deep behind an opening 2 m by 1.8 m).
   */
  light: 0.09,
  /** The ceiling's share, the most of the room's inner surfaces (section 8.5). */
  ceilingLight: 0.11,
  /**
   * How many times the estimates above the room is drawn at. The renderer lights a surface from its
   * albedo and the sun alone, with no adaptation of the eye, so the estimates themselves draw a pure
   * black void at walking height and nothing of the room shows through the opening. At this gain
   * the back wall, the beams and the floor read, at about half the brightness of the front wall at
   * noon (section 8.5, "What the room is drawn at"). Art direction, not a measure.
   */
  drawnGain: 13,
  /**
   * The floor's height over the ground. The ground runs on under the house at height 0, and a floor
   * in the same plane lost the depth test to it; 5 cm keep them apart in a 24-bit depth buffer to
   * 200 m, beyond which no room shows, and the sill hides the step from the street.
   */
  floor: 0.05,
  /** A corner shop's side opening, 1 m back and 2 m wide, and half a metre clear of the partition. */
  clear: 0.5,
  /** Main beams across the room on the bents, a bay apart, the joists front to back between them. */
  bay: 2.5,
  beam: { width: 0.25, height: 0.3 },
  joist: { width: 0.1, height: 0.12, spacing: 0.5 },
  /** The corbel (sleutelstuk) under each main beam's ends. */
  corbel: { height: 0.5, reach: 0.35 },
} as const;

/** The front room in the house frame: x inward from the street, y up, z to the right. */
export interface ShopRoom {
  /**
   * Where the front wall's reveals start: the ground storey's front, or behind a stepped or spout
   * slab the body's own front, whose shop openings the slab's are cut through to.
   */
  readonly outer: number;
  /** The front wall's inner face. */
  readonly inner: number;
  /** The partition to the back room. */
  readonly back: number;
  /** Half the width between the side walls' inner faces. */
  readonly half: number;
  /** Half the width between the side walls' outer faces. */
  readonly outerHalf: number;
  readonly height: number;
}

export interface ShopRoomStyles {
  readonly floor: VertexStyle;
  readonly wall: VertexStyle;
  readonly wood: VertexStyle;
  readonly ceiling: VertexStyle;
}

export interface ShopRoomColors {
  readonly floor: LinearRGB;
  readonly wall: LinearRGB;
  readonly wood: LinearRGB;
  readonly ceiling: LinearRGB;
}

const scaled = (c: LinearRGB, k: number): LinearRGB => [c[0] * k, c[1] * k, c[2] * k];

/**
 * A floor of fired clay tiles, brick walls and an oak partition under the room's light, and the oak
 * ceiling, beams and joists under the ceiling's. The renderer shades a face turned down to its class's
 * p10 (surfaceShading.ts), as if the ceiling were a wall out of the sun; the estimate already gives the
 * ceiling its own light, so that shade is taken back out.
 */
export function shopRoomColors(palette: PaletteModel): ShopRoomColors {
  const oak = palette.shipHull.median.linear;
  const down = computeShadeRatios(palette.shipHull).low;
  const drawn = SHOP_ROOM.light * SHOP_ROOM.drawnGain;
  return {
    floor: scaled(palette.roofTile.median.linear, drawn),
    wall: scaled(palette.wallBrick.median.linear, drawn),
    wood: scaled(oak, drawn),
    ceiling: scaled(oak, (SHOP_ROOM.ceilingLight * SHOP_ROOM.drawnGain) / down),
  };
}

export function hasShopRoom(shop: ShopFronts): boolean {
  return shop.front || shop.left || shop.right;
}

/* The flue stands in the wall between the front and back rooms (S16-253): the front-most stack's. */
function partitionAt(body: Body, chimneys: readonly Chimney[]): number {
  const at = chimneys.length > 0 ? Math.min(...chimneys.map((c) => c.at)) : 0.5;
  return body.x0 + at * (body.depth - body.x0);
}

/** The front room behind a ground storey whose front stands at `outer`. */
export function shopRoomOf(
  body: Body,
  outer: number,
  params: Pick<HouseParams, 'storeyHeight' | 'chimneys'>
): ShopRoom {
  const front = Math.max(outer, body.x0);
  const inner = front + SHOP_ROOM.wall;
  const side = OPENING.shop.side;
  const least = inner + side.fromFront + side.width + SHOP_ROOM.clear;
  const back = Math.min(
    Math.max(partitionAt(body, params.chimneys), least),
    body.depth - SHOP_ROOM.wall
  );
  return {
    outer: front,
    inner,
    back,
    half: body.half - SHOP_ROOM.wall,
    outerHalf: body.half,
    height: params.storeyHeight,
  };
}

/* The face of the box from `min` to `max` that looks along `dir`, one of the six axis directions. */
function boxFace(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  min: Vec3,
  max: Vec3,
  dir: Vec3
): void {
  const a = dir.findIndex((v) => v !== 0);
  const [b, c] = [(a + 1) % 3, (a + 2) % 3];
  const at = (u: number, v: number): Vec3 => {
    const p = [0, 0, 0];
    p[a] = dir[a]! > 0 ? max[a]! : min[a]!;
    p[b] = u;
    p[c] = v;
    return p as unknown as Vec3;
  };
  const pts = [
    at(min[b]!, min[c]!),
    at(max[b]!, min[c]!),
    at(max[b]!, max[c]!),
    at(min[b]!, max[c]!),
  ];
  orientedFace(builder, style, frame, pts, dir);
}

const [DOWN, UP, TO_STREET, TO_BACK, LEFT, RIGHT] = [
  [0, -1, 0],
  [0, 1, 0],
  [-1, 0, 0],
  [1, 0, 0],
  [0, 0, -1],
  [0, 0, 1],
] as const satisfies readonly Vec3[];

/* A wall's inner face with holes, meshed in its own panel's coordinates. */
function wallWithHoles(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  panel: Panel,
  size: Vec2,
  holes: readonly Hole[]
): void {
  for (const fan of meshRectWall(size[0], size[1], holes).wall) {
    builder.polygon(
      style,
      frame,
      fan.map((p) => panelPoint(panel, p[0], p[1]))
    );
  }
}

/* The shop openings of the ground storey, each as its span along its wall in house coordinates. */
interface Span {
  readonly a: number;
  readonly b: number;
  readonly opening: Opening;
}

type Spans = Record<'front' | 'left' | 'right', Span[]>;

const hole = (u0: number, u1: number, o: Opening): Hole => ({
  x: u0,
  y: o.y,
  width: u1 - u0,
  height: o.height,
  cuts: [],
});

function spans(room: ShopRoom, openings: HouseOpenings, body: Pick<Body, 'x0' | 'depth'>): Spans {
  const shops = (os: readonly Opening[]) =>
    os.filter((o) => o.kind === 'shop' && o.y + o.height <= room.height + 1e-6);
  const within = (s: Span) => s.a >= room.inner + 1e-6 && s.b <= room.back - 1e-6;
  const span = (a: number, o: Opening): Span => ({ a, b: a + o.width, opening: o });
  return {
    front: shops(openings.front).map((o) => span(-room.outerHalf + o.x, o)),
    left: shops(openings.left)
      .map((o) => span(body.depth - o.x - o.width, o))
      .filter(within),
    right: shops(openings.right)
      .map((o) => span(body.x0 + o.x, o))
      .filter(within),
  };
}

/* The front and side walls' inner faces with the shop openings left open, the partition, floor and ceiling. */
function buildWalls(
  builder: MeshBuilder,
  styles: ShopRoomStyles,
  frame: Frame,
  room: ShopRoom,
  s: Spans
): void {
  const { inner, back, half, height } = room;
  const front = verticalPanel([inner, 0, half], TO_BACK);
  const holesFront = s.front.map((p) => hole(half - p.b, half - p.a, p.opening));
  wallWithHoles(builder, styles.wall, frame, front, [2 * half, height], holesFront);
  const left = verticalPanel([inner, 0, -half], RIGHT);
  const holesLeft = s.left.map((p) => hole(p.a - inner, p.b - inner, p.opening));
  wallWithHoles(builder, styles.wall, frame, left, [back - inner, height], holesLeft);
  const right = verticalPanel([back, 0, half], LEFT);
  const holesRight = s.right.map((p) => hole(back - p.b, back - p.a, p.opening));
  wallWithHoles(builder, styles.wall, frame, right, [back - inner, height], holesRight);
  const [floor, top] = [SHOP_ROOM.floor, [back, height, half] as const];
  boxFace(builder, styles.wood, frame, [back, 0, -half], top, TO_STREET);
  boxFace(builder, styles.floor, frame, [inner, floor, -half], [back, floor, half], UP);
  boxFace(builder, styles.ceiling, frame, [inner, height, -half], top, DOWN);
}

/* Jambs, sill and head through the wall, from its outer face (t = 0) to the room (t = 1). */
function buildReveal(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  across: (u: number, y: number, t: number) => Vec3,
  span: Span
): void {
  const { a, b, opening: o } = span;
  const [y0, y1] = [o.y, o.y + o.height];
  const [p0, p1] = [across(0, 0, 0), across(1, 0, 0)];
  const along: Vec3 = [p1[0] - p0[0], p1[1] - p0[1], p1[2] - p0[2]];
  const strip = (p: Vec2, q: Vec2, facing: Vec3): void => {
    const pts = [across(...p, 0), across(...q, 0), across(...q, 1), across(...p, 1)];
    orientedFace(builder, style, frame, pts, facing);
  };
  strip([a, y0], [a, y1], along);
  strip([b, y0], [b, y1], [-along[0], -along[1], -along[2]]);
  strip([a, y0], [b, y0], UP);
  strip([a, y1], [b, y1], DOWN);
}

function buildReveals(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  room: ShopRoom,
  s: Spans
): void {
  const { outer, inner, half, outerHalf } = room;
  const thick = outerHalf - half;
  for (const sp of s.front)
    buildReveal(builder, style, frame, (u, y, t) => [outer + t * (inner - outer), y, u], sp);
  for (const sp of s.left)
    buildReveal(builder, style, frame, (u, y, t) => [u, y, -outerHalf + t * thick], sp);
  for (const sp of s.right)
    buildReveal(builder, style, frame, (u, y, t) => [u, y, outerHalf - t * thick], sp);
}

/** Where the main beams stand: evenly, about a bay apart, from the front wall to the last against the partition. */
export function beamLines(room: Pick<ShopRoom, 'inner' | 'back'>): number[] {
  const last = room.back - SHOP_ROOM.beam.width / 2;
  const bays = Math.max(1, Math.round((last - room.inner) / SHOP_ROOM.bay));
  return Array.from(
    { length: bays },
    (_, k) => room.inner + ((k + 1) * (last - room.inner)) / bays
  );
}

/*
 * A main beam across the room with a corbel under each end. Beams, corbels and joists are closed but
 * for their ends in the walls, their tops against the ceiling included, so each bounds a solid.
 */
function buildBeam(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  room: ShopRoom,
  x: number
): void {
  const { beam, corbel } = SHOP_ROOM;
  const { half, height } = room;
  const [x0, x1, y0] = [x - beam.width / 2, x + beam.width / 2, height - beam.height];
  const ends = [DOWN, UP, TO_STREET, TO_BACK];
  for (const dir of ends) boxFace(builder, style, frame, [x0, y0, -half], [x1, height, half], dir);
  const reach = half - corbel.reach;
  for (const [z0, z1, inward] of [
    [-half, -reach, RIGHT],
    [reach, half, LEFT],
  ] as const) {
    for (const dir of [...ends, inward])
      boxFace(builder, style, frame, [x0, y0 - corbel.height, z0], [x1, y0, z1], dir);
  }
}

function buildCeiling(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  room: ShopRoom
): void {
  const { beam, joist } = SHOP_ROOM;
  const { half, height } = room;
  const lines = beamLines(room);
  const count = Math.max(1, Math.floor((2 * half) / joist.spacing));
  lines.forEach((x, k) => {
    buildBeam(builder, style, frame, room, x);
    const from = k === 0 ? room.inner : lines[k - 1]! + beam.width / 2;
    for (let j = 0; j < count; j++) {
      const z = -half + ((j + 0.5) * 2 * half) / count;
      const [min, max] = [
        [from, height - joist.height, z - joist.width / 2],
        [x - beam.width / 2, height, z + joist.width / 2],
      ] as const;
      for (const dir of [DOWN, UP, LEFT, RIGHT]) boxFace(builder, style, frame, min, max, dir);
    }
  });
}

/**
 * The front room behind a shop (docs/plans/houses.md, section 8.5): its floor, walls and oak
 * partition, the ceiling on main beams with corbels and joists, and reveals through the walls
 * around its shop openings. Every face turns into the room, so from the street only what the
 * openings show is drawn.
 */
export function buildShopRoom(
  builder: MeshBuilder,
  styles: ShopRoomStyles,
  frame: Frame,
  room: ShopRoom,
  openings: HouseOpenings,
  body: Pick<Body, 'x0' | 'depth'>
): void {
  const s = spans(room, openings, body);
  buildWalls(builder, styles, frame, room, s);
  buildReveals(builder, styles.wall, frame, room, s);
  buildCeiling(builder, styles.ceiling, frame, room);
}
